import CssBaseline from '@mui/material/CssBaseline';
import { ThemeProvider } from '@mui/material/styles';
import {
  MutationCache,
  QueryCache,
  QueryClient,
  QueryClientProvider,
} from '@tanstack/react-query';
import ReactDOM from 'react-dom';
import { HelmetProvider } from 'react-helmet-async';
import './amplify';
import { App } from './app-root';
import { reportAgentQueryResult } from './components/alliance/agent-access';
import {
  reportRequestFailure,
  reportRequestSuccess,
} from './components/alliance/connection-monitor';
import { AuthProvider } from './contexts/auth-context';
import { materialTheme } from './theme';
import { captureError } from './utils/capture-error';

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Retry once, but not 4xx: the API answered, and asking again only
      // doubles the error logs and delays the message
      retry: (failureCount, error) => {
        const status = (error as { response?: { status?: number } })?.response
          ?.status;
        return failureCount < 1 && !(status && status >= 400 && status < 500);
      },
      refetchOnWindowFocus: false,
    },
  },
  queryCache: new QueryCache({
    onError: (error, query) => {
      // Feed the connection banner and the impersonation access check
      // (queries with meta.agentAccess false opt out) before captureError
      // serializes the error
      reportRequestFailure(error);
      if (query.meta?.agentAccess !== false) {
        reportAgentQueryResult({ queryKey: query.queryKey, error });
      }
      captureError({ data: { error } });
    },
    onSuccess: (data, query) => {
      reportRequestSuccess();
      if (query.meta?.agentAccess !== false) {
        reportAgentQueryResult({ queryKey: query.queryKey });
      }
    },
  }),
  // Mutations already report their own errors in onError; this only feeds
  // the connection banner
  mutationCache: new MutationCache({
    onError: (error) => reportRequestFailure(error),
    onSuccess: () => reportRequestSuccess(),
  }),
});

ReactDOM.render(
  <ThemeProvider theme={materialTheme}>
    <CssBaseline />

    <QueryClientProvider client={queryClient}>
      <HelmetProvider>
        <AuthProvider>
          <App />
        </AuthProvider>
      </HelmetProvider>
    </QueryClientProvider>
  </ThemeProvider>,
  document.getElementById('root'),
);
